import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Vault, Eye, Flame, Wrench, Building2, Users, Clock, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

export default function Products() {
  const [activeCategory, setActiveCategory] = useState('all');

  const products = [
    {
      id: 1,
      category: 'vault',
      name: 'High-Security Vault Doors',
      subtitle: 'Bank-Grade Protection',
      description: 'UL-rated vault doors engineered for maximum security in financial institutions, data centers, and high-value storage facilities.',
      features: [
        'UL TL-30x6 certified protection',
        'Composite steel and concrete construction',
        'Time-delay and dual-control mechanisms',
        'Advanced relocking systems',
        'Up to 4-hour fire rating',
        'Custom sizing available'
      ],
      specs: {
        'Construction': 'Multi-layer composite steel',
        'Fire Rating': 'Up to 4 hours (Class 350)',
        'Lock Types': 'Electronic, Mechanical, Biometric',
        'Thickness': '6" - 24"',
        'Weight': '1,500 - 8,000 lbs',
        'Warranty': '10 years comprehensive'
      },
      applications: ['Banks', 'Jewelry Stores', 'Data Centers', 'Museums'],
      image: 'https://images.unsplash.com/photo-1582139329536-e7284fece509?w=800&q=85'
    },
    {
      id: 2,
      category: 'biometric',
      name: 'Biometric Access Control Systems',
      subtitle: 'Multi-Factor Authentication',
      description: 'State-of-the-art biometric systems combining fingerprint, facial recognition, and iris scanning for unparalleled access security.',
      features: [
        'Fingerprint, facial & iris recognition',
        '<1 second recognition speed',
        '99.9%+ accuracy rate',
        'Supports up to 10,000 users',
        'Anti-spoofing technology',
        'Integration with existing systems'
      ],
      specs: {
        'Recognition Speed': '<1 second',
        'Accuracy': '99.9%+',
        'User Capacity': 'Up to 10,000',
        'Authentication': 'Multi-factor (2FA/3FA)',
        'Network': 'TCP/IP, WiFi, BLE',
        'Operating Temp': '-20°C to +60°C'
      },
      applications: ['Government Facilities', 'Hospitals', 'Corporate Offices', 'Research Labs'],
      image: 'https://images.unsplash.com/photo-1668609537021-f377356358f9?w=800&q=85'
    },
    {
      id: 3,
      category: 'armored',
      name: 'Armored Security Doors',
      subtitle: 'Blast & Bullet Resistant',
      description: 'Military-grade armored doors providing maximum protection against ballistic threats, blast forces, and forced entry attempts.',
      features: [
        'Ballistic rating up to NIJ Level IV',
        'Blast-resistant construction (UFC 4-010-01)',
        'Forced-entry rating: RC3-RC6',
        'Weatherproof and corrosion-resistant',
        'Custom finishes available',
        'CE, UL, ISO certified'
      ],
      specs: {
        'Ballistic Rating': 'NIJ Level III / IV',
        'Blast Rating': 'UFC 4-010-01 compliant',
        'Material': 'Armored steel composite',
        'Thickness': '50mm - 120mm',
        'Weight': '300 - 1,200 kg',
        'Certifications': 'CE, UL, ISO, ASTM'
      },
      applications: ['Embassies', 'Military Bases', 'Critical Infrastructure', 'High-Risk Facilities'],
      image: 'https://images.unsplash.com/photo-1705776919715-fa052f797923?w=800&q=85'
    },
    {
      id: 4,
      category: 'industrial',
      name: 'Industrial Steel Doors',
      subtitle: 'Commercial & Warehouse',
      description: 'Heavy-duty industrial doors designed for high-traffic commercial and warehouse environments with superior durability.',
      features: [
        'Heavy-gauge steel construction',
        'Roller shutter and sectional options',
        'High-speed operation available',
        'Weather sealing and insulation',
        'Remote control operation',
        'Low maintenance requirements'
      ],
      specs: {
        'Material': '18-14 gauge steel',
        'Max Size': 'Up to 40\' x 40\'',
        'Operation Speed': 'Up to 48" per second',
        'Wind Load': 'Class 1-5 rated',
        'Insulation': 'R-value up to 18',
        'Cycles': '100,000+ rated'
      },
      applications: ['Warehouses', 'Distribution Centers', 'Loading Docks', 'Manufacturing'],
      image: 'https://images.unsplash.com/photo-1504376830547-506dedfe1fe9?w=800&q=85'
    },
    {
      id: 5,
      category: 'access',
      name: 'Integrated Access Management',
      subtitle: 'Multi-Zone Control',
      description: 'Comprehensive access control platform managing unlimited doors, users, and security zones from a centralized system.',
      features: [
        'Unlimited door and user support',
        'Card, mobile & PIN authentication',
        'Real-time monitoring and alerts',
        'CCTV and alarm integration',
        'Cloud or on-premise deployment',
        'Mobile apps (iOS & Android)'
      ],
      specs: {
        'Doors Supported': 'Unlimited',
        'User Capacity': 'Unlimited',
        'Authentication': 'Card, Mobile, PIN, Biometric',
        'Integration': 'REST API, ONVIF, Wiegand',
        'Reporting': 'Custom analytics dashboard',
        'Mobile Support': 'iOS 14+, Android 10+'
      },
      applications: ['Multi-Site Enterprises', 'Hospitals', 'Universities', 'Government Buildings'],
      image: 'https://images.unsplash.com/photo-1697382608786-bcf4c113b86e?w=800&q=85'
    },
    {
      id: 6,
      category: 'fire',
      name: 'Fire-Resistant Security Doors',
      subtitle: 'Life Safety Compliance',
      description: 'Fire-rated security doors combining life safety requirements with high-security features for complete protection.',
      features: [
        'Fire ratings: FD30 to FD240',
        'Smoke and temperature seals',
        'Panic hardware compatible',
        'Security + fire compliance',
        'Intumescent seals included',
        'Annual inspection service'
      ],
      specs: {
        'Fire Rating': 'FD30, FD60, FD120, FD240',
        'Standards': 'BS 476, EN 1634, NFPA',
        'Core Material': 'Mineral fiber or vermiculite',
        'Max Temp Rise': '250°F on unexposed side',
        'Hardware': 'Fire-rated locks and closers',
        'Inspection': 'Annual certification'
      },
      applications: ['Hospitals', 'Hotels', 'Schools', 'Data Centers'],
      image: 'https://images.pexels.com/photos/965878/pexels-photo-965878.jpeg?w=800&q=85'
    },
    {
      id: 7,
      category: 'maintenance',
      name: 'Maintenance & Support Services',
      subtitle: '24/7 Emergency Response',
      description: 'Comprehensive maintenance programs ensuring your security systems remain operational and compliant year-round.',
      features: [
        '<4 hour emergency response',
        'Scheduled preventive maintenance',
        'Parts replacement and upgrades',
        'Compliance documentation',
        'Manufacturer-certified technicians',
        'Annual inspection reports'
      ],
      specs: {
        'Response Time': '<4 hours emergency',
        'Coverage': 'UK & USA nationwide',
        'Service Plans': 'Annual or multi-year',
        'Availability': '24/7/365',
        'Technicians': 'Factory certified',
        'Documentation': 'Full compliance records'
      },
      applications: ['All Security Installations', 'Banks', 'Government', 'Commercial'],
      image: 'https://images.pexels.com/photos/64609/pexels-photo-64609.jpeg?w=800&q=85'
    },
    {
      id: 8,
      category: 'special',
      name: 'Attack-Resistant Openings',
      subtitle: 'Active Threat Protection',
      description: 'Advanced protective assemblies tested to withstand prolonged physical attacks, providing critical time for emergency response.',
      features: [
        'Tested to 5-aa10 forced entry standard',
        'Withstands 4+ minutes of attack',
        'SchoolGuard Glass™ SG5 glazing',
        'Standard door compatibility',
        'Retrofit kits available',
        'FBI Active Shooter Report compliant'
      ],
      specs: {
        'Attack Duration': '4+ minutes resistance',
        'Test Standard': '5-aa10 forced entry',
        'Glazing': 'SG5 attack-resistant',
        'Applications': 'Wood & hollow metal doors',
        'Retrofit': 'Available for existing doors',
        'Hardware': 'High-security rated'
      },
      applications: ['Schools', 'Public Buildings', 'Healthcare', 'Retail'],
      image: 'https://images.pexels.com/photos/8134793/pexels-photo-8134793.jpeg?w=800&q=85'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Products', icon: Building2 },
    { id: 'vault', name: 'Vault Systems', icon: Vault },
    { id: 'biometric', name: 'Biometric', icon: Lock },
    { id: 'armored', name: 'Armored Doors', icon: Shield },
    { id: 'industrial', name: 'Industrial', icon: Building2 },
    { id: 'access', name: 'Access Control', icon: Eye },
    { id: 'fire', name: 'Fire-Resistant', icon: Flame },
    { id: 'maintenance', name: 'Maintenance', icon: Wrench }
  ];

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="flex flex-col">
      {/* Header */}
      <section className="py-16 bg-card border-b border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="outline" className="text-accent border-accent mb-4">
              Our Products
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-semibold mb-6">Complete Security Solutions Portfolio</h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              From vault doors to access control systems, explore our comprehensive range of 
              high-security products designed to protect what matters most.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-16 z-40 bg-background border-b border-border py-4">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className="whitespace-nowrap"
              >
                <cat.icon className="h-4 w-4 mr-2" />
                {cat.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden h-full flex flex-col">
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-primary text-primary-foreground">
                        {product.subtitle}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader>
                    <CardTitle className="text-xl">{product.name}</CardTitle>
                    <CardDescription>{product.description}</CardDescription>
                  </CardHeader>

                  <CardContent className="flex-grow space-y-6">
                    {/* Features */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 flex items-center">
                        <CheckCircle2 className="h-4 w-4 mr-2 text-accent" />
                        Key Features
                      </h4>
                      <ul className="grid grid-cols-2 gap-2">
                        {product.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="text-xs text-muted-foreground flex items-start">
                            <span className="text-accent mr-1">•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Applications */}
                    <div>
                      <h4 className="text-sm font-semibold mb-2 flex items-center">
                        <Building2 className="h-4 w-4 mr-2 text-accent" />
                        Applications
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {product.applications.map((app, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {app}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Specs Preview */}
                    <div className="pt-4 border-t border-border">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {Object.entries(product.specs).slice(0, 4).map(([key, value], idx) => (
                          <div key={idx}>
                            <span className="text-muted-foreground">{key}:</span>
                            <span className="ml-1 font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA */}
                    <Link to="/contact" className="block">
                      <Button className="w-full" variant="outline">
                        Request Quote
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-card border-t border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">Why Choose Green Flux Products</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Industry-leading quality, comprehensive testing, and unmatched support.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Certified Quality</h3>
                <p className="text-sm text-muted-foreground">
                  All products meet or exceed UL, ISO, CE, and industry-specific certifications.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Expert Installation</h3>
                <p className="text-sm text-muted-foreground">
                  Factory-certified technicians ensure proper installation and integration.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 mb-4">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">24/7 Support</h3>
                <p className="text-sm text-muted-foreground">
                  Round-the-clock emergency support and maintenance across UK and USA.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
